// React
import React, { useEffect, useState } from 'react'

// Next
import Link from 'next/link'
import { useRouter } from 'next/router'

// Components
import { CustomLoader } from '@/components/blocks/custom-loader'
import { AnimatedInput } from '@/components/blocks/animated-input'
import { RawMemeTemplate } from '@/components/memes/templates/raw-meme-template'

// Types
import { MemeTemplate } from '@supermeme-ai/types'

// Utils
import { shuffleArray } from '@supermeme-ai/utils'

// External
import { Session } from '@supabase/supabase-js'
import { SearchIcon } from 'lucide-react'
import useSWR from 'swr'
import { useTranslation } from 'next-i18next'

type SearchProps = {
  session: Session
  showEditButton?: boolean
  initialQuery?: string
  pushQueryToUrl?: boolean
  onSelect?: (template: MemeTemplate) => void
}

const fetcher = (url: string, token: string) =>
  fetch(url, { headers: { token } }).then((res) => res.json())

const Search: React.FC<SearchProps> = ({
  session,
  showEditButton,
  initialQuery,
  pushQueryToUrl,
  onSelect,
}) => {
  const { t } = useTranslation('components/image-search')
  const router = useRouter()
  const [inputValue, setInputValue] = useState('')
  const [searchQuery, setSearchQuery] = useState(initialQuery || '')

  const { data, error, isValidating } = useSWR(
    !!searchQuery
      ? [`/api/search?searchQuery=${searchQuery}`, session?.access_token]
      : null,
    ([url, token]) => fetcher(url, token),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  const loading = isValidating
  const templates = data?.memeTemplates || []

  const submitSearchForm = async (evt: React.FormEvent) => {
    evt.preventDefault()
    await searchMemes(inputValue)
  }

  const searchMemes = async (query: string) => {
    if (query) {
      setSearchQuery(query)
      if (pushQueryToUrl) {
        router.push(`/search?query=${query}`, undefined, { shallow: true })
      }
    }
  }

  useEffect(() => {
    if (initialQuery) {
      setInputValue(initialQuery)
      searchMemes(initialQuery)
    } else {
      const exampleSearchTerms = t('exampleSearchTerms', {
        returnObjects: true,
      }) as string[]
      const shuffledArray = shuffleArray(exampleSearchTerms)
      setSearchQuery(shuffledArray[0])
    }
  }, [t])

  return (
    <div className='mx-auto space-y-6 md:container'>
      <form onSubmit={submitSearchForm}>
        <div className='mx-auto max-w-4xl space-y-4 text-center'>
          <label
            htmlFor='text'
            className='w-full text-center text-xl font-semibold md:text-2xl'
          >
            <span className='block whitespace-nowrap md:inline'>
              {t('searchBar.label.part1')}
            </span>{' '}
            <span className='block whitespace-nowrap md:inline'>
              {t('searchBar.label.part2')}{' '}
              <Link
                href='/search-supported-languages'
                className='whitespace-nowrap underline'
              >
                {t('searchBar.label.part3')}
              </Link>
              {t('searchBar.label.part4')}
            </span>
          </label>

          <div className='flex flex-col items-start space-x-0 space-y-2 md:flex-row md:space-x-2 md:space-y-0'>
            <div className='w-full'>
              <AnimatedInput
                id='meme-search'
                name='meme-search'
                label={t('searchBar.input.label')}
                value={inputValue}
                placeholders={
                  t('searchBar.input.placeholders', {
                    returnObjects: true,
                  }) as string[]
                }
                required
                buttonLabel={t('searchBar.input.button')}
                buttonIcon={
                  <SearchIcon
                    className='h-5 w-5 text-white'
                    aria-hidden='true'
                  />
                }
                buttonType='submit'
                loading={loading}
                onChange={(value) => setInputValue(value)}
              />
            </div>
          </div>
        </div>
      </form>

      {!loading && templates.length > 0 && (
        <div className='grid grid-cols-1 gap-2 border-t border-gray-200 pt-6 lg:grid-cols-3 lg:gap-4'>
          {templates.map((template, i) => (
            <div key={i} className='rounded-lg bg-gray-100 p-4'>
              <RawMemeTemplate
                {...template}
                showEditButton={showEditButton}
                onSelect={onSelect}
              />
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className='flex items-center justify-center py-12'>
          <CustomLoader />
        </div>
      )}
    </div>
  )
}

export default Search
